import React, { useState } from 'react';
import clsx from 'clsx';

import { useUpdateEffect } from '../../hooks';

export type TabsProps<T extends string = string> = {
  tabs: TabType<T>[];
  className?: string;
  tabClassName?: string;
  tabsClassName?: string;
  contentClassName?: string;
  tabClassNameActive?: string;
  tabClassNameNotActive?: string;

  activeTab?: string;
  defaultActiveTab?: T;
  onChange?: (newTab: TabType<T>['name']) => void;

  children?: React.ReactNode | React.ReactNode[];
};

type TabProps = {
  name: string;
  children?: React.ReactNode;
};

export const Tab: React.FC<TabProps> = ({ children }) => {
  return <>{children}</>;
};

export const Tabs = <T extends string = string>({
  activeTab: value,
  defaultActiveTab,
  tabs,
  onChange,
  children,
  className,

  tabClassName = 'border-b-2 border-main',
  tabsClassName,
  contentClassName,
  tabClassNameActive,
  tabClassNameNotActive,
}: TabsProps<T>) => {
  const initialTab = defaultActiveTab ?? tabs?.[0]?.name;
  const [localActiveTab, setActiveTab] = useState(initialTab);
  const activeTab = value ?? localActiveTab;

  const handleChangeTab = (newTab: T) => {
    onChange?.(newTab);
    setActiveTab(newTab);
  };

  useUpdateEffect(
    () => {
      if (tabs.some(t => t.name === activeTab)) return;
      handleChangeTab(initialTab);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTab, tabs]
  );

  const ActiveTab = React.Children.toArray(children)
    .map(c => c as React.ReactElement<TabProps>)
    .filter(c => c?.type === Tab && c.props.name === activeTab);

  return (
    <div className={clsx('flex flex-col', className)}>
      <div
        className={clsx(
          tabsClassName,
          'flex gap-x-md border-b border-solid border-border'
        )}
      >
        {tabs.map(tab => (
          <div
            key={tab.name}
            onClick={() => handleChangeTab(tab.name)}
            className={clsx(
              tabClassName,
              'cursor-pointer transition-def',
              'py-sm px-md text-medium',
              activeTab === tab.name
                ? ['font-medium border-solid', [tabClassNameActive ?? '']]
                : ['font-normal', tabClassNameNotActive]
            )}
          >
            {tab.label}
          </div>
        ))}
      </div>
      <div className={contentClassName}>{ActiveTab}</div>
    </div>
  );
};
